import React, { useState, useRef, Component, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaYoutube, FaLinkedin } from 'react-icons/fa';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div>Something went wrong.</div>;
    }
    return this.props.children;
  }
}

// Captcha component with fallback for Enterprise reCAPTCHA
const SafeReCAPTCHA = ({ onChange, recaptchaRef }) => {
  useEffect(() => {
    // Load the reCAPTCHA Enterprise script
    const loadRecaptchaScript = () => {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/enterprise.js?render=6LcHGz0rAAAAAIRSYbmGUx_RZMGJh54tdBWk29qG';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.grecaptcha) {
          window.grecaptcha.enterprise.ready(() => {
            console.log("reCAPTCHA Enterprise is ready");
          });
        }
      };
      script.onerror = () => {
        console.error("Error loading reCAPTCHA Enterprise script");
      };
      document.head.appendChild(script);
    };
    
    loadRecaptchaScript();
    
    return () => {
      // Remove the script when component unmounts
      const script = document.querySelector('script[src^="https://www.google.com/recaptcha/enterprise.js"]');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);
  
  return (
    <ErrorBoundary fallback={<div className="captcha-error">CAPTCHA temporarily unavailable</div>}>
      <div id="recaptcha-container"></div>
    </ErrorBoundary>
  );
};

const ContactContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-lavender);
  }
`;

const IntroText = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const ContactForm = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: var(--spacing-xl);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const Label = styled.label`
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--text);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--grey-border);
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--accent-lavender);
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--grey-border);
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    border-color: var(--accent-lavender);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--grey-border);
  border-radius: 4px;
  font-size: 1rem;
  min-height: 120px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--accent-lavender);
    outline: none;
  }
`;

const Button = styled.button`
  background-color: var(--accent-lavender);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-lavender);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const InfoCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: var(--spacing-lg);
`;

const InfoTitle = styled.h3`
  margin-bottom: var(--spacing-md);
  font-size: 1.2rem;
  color: var(--accent-lavender);
`;

const ContactDetail = styled.div`
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: flex-start;
`;

const DetailIcon = styled.span`
  display: inline-block;
  margin-right: var(--spacing-sm);
  color: var(--accent-turquoise);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const SocialLink = styled.a`
  color: var(--text);
  font-size: 1.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--accent-turquoise);
  }
`;

const FormSuccess = styled.div`
  background-color: rgba(62, 221, 226, 0.1);
  color: var(--accent-turquoise);
  padding: var(--spacing-md);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const FormError = styled.div`
  background-color: rgba(255, 99, 71, 0.1);
  color: tomato;
  padding: var(--spacing-md);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const MapFrame = styled.div`
  height: 250px;
  border-radius: 8px;
  overflow: hidden;
  margin-top: var(--spacing-md);
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAText = styled.p`
  max-width: 800px;
  margin: 0 auto var(--spacing-lg);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const RecaptchaContainer = styled.div`
  margin-bottom: var(--spacing-md);
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (value) {
      setCaptchaError(false);
    }
  };

  const executeRecaptcha = () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        try {
          window.grecaptcha.enterprise.ready(async () => {
            const token = await window.grecaptcha.enterprise.execute('6LcHGz0rAAAAAIRSYbmGUx_RZMGJh54tdBWk29qG', {action: 'submit_contact'});
            resolve(token);
          });
        } catch (error) {
          console.error("reCAPTCHA execution failed:", error);
          reject(error);
        }
      } else {
        console.error("reCAPTCHA Enterprise not loaded");
        reject(new Error("reCAPTCHA Enterprise not loaded"));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError(false);
    
    try {
      // Execute reCAPTCHA and get token
      const token = await executeRecaptcha();
      
      // Continue with form submission if we have the token
      if (token) {
        // Check for required fields before submitting
        if (formData.name && formData.email && formData.message) {
          // Create payload for API
          const payload = {
            name: formData.name,
            email: formData.email,
            service: formData.service,
            message: formData.message,
            token: token
          };
          
          // Save directly to Firestore instead of calling the API
          try {
            // Get Firestore instance
            const firestore = getFirestore(getApp());
            
            // Add document to contact_submissions collection
            await addDoc(collection(firestore, 'contact_submissions'), {
              ...payload,
              createdAt: new Date()
            });
            
            setSubmitted(true);
            setError(false);
            // Reset form after submission
            setFormData({
              name: '',
              email: '',
              service: '',
              message: ''
            });
            setCaptchaValue(null);
          } catch (apiError) {
            console.error('Form submission error:', apiError);
            setError(true);
          }
        } else {
          setError(true);
        }
      }
    } catch (error) {
      console.error("Form submission failed:", error);
      setCaptchaError(true);
    }
  };

  return (
    <ContactContainer>
      <Container>
        <PageTitle>Let's <span>Connect</span></PageTitle>
        <IntroText>
          Ready to build something remarkable? We're here to help your travel business innovate, grow, and create unforgettable experiences.
        </IntroText>
        
        <ContactGrid>
          <ContactForm onSubmit={handleSubmit}>
            {submitted && (
              <FormSuccess>
                <p>Thank you for your message! We'll get back to you soon.</p>
              </FormSuccess>
            )}
            
            {error && (
              <FormError>
                <p>Please fill out all required fields.</p>
              </FormError>
            )}
            
            <FormGroup>
              <Label htmlFor="name">Your Name *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="service">I'm Interested In</Label>
              <Select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
              >
                <option value="">Select a service</option>
                <option value="travel-marketing">Travel Marketing</option>
                <option value="tourism-development">Tourism Development</option>
                <option value="digital-tools">Digital Tools</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="message">Your Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <RecaptchaContainer>
              <SafeReCAPTCHA 
                recaptchaRef={recaptchaRef}
                onChange={handleCaptchaChange}
              />
              {captchaError && (
                <FormError>
                  <p>Please complete the reCAPTCHA verification.</p>
                </FormError>
              )}
            </RecaptchaContainer>
            
            <Button type="submit">Send Message</Button>
          </ContactForm>
          
          <ContactInfo>
            <InfoCard>
              <InfoTitle>Contact Information</InfoTitle>
              
              <ContactDetail>
                <DetailIcon>📍</DetailIcon>
                <div>
                  <strong>Location</strong>
                  <p>Paris, France</p>
                </div>
              </ContactDetail>
              
              <ContactDetail>
                <DetailIcon>📧</DetailIcon>
                <div>
                  <strong>Email</strong>
                  <p>alex@passportcreative.co</p>
                </div>
              </ContactDetail>
              
              <ContactDetail>
                <DetailIcon>🌐</DetailIcon>
                <div>
                  <strong>Social</strong>
                  <SocialLinks>
                    <SocialLink href="https://linkedin.com/company/passportcreative" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </SocialLink>
                    <SocialLink href="https://www.youtube.com/@passportcreative" target="_blank" rel="noopener noreferrer">
                      <FaYoutube />
                    </SocialLink>
                  </SocialLinks>
                </div>
              </ContactDetail>
            </InfoCard>
            
            <InfoCard>
              <InfoTitle>Working Hours</InfoTitle>
              <p>Monday - Friday: 9:00 AM - 6:00 PM CET</p>
              <p>We respond to all inquiries within 24 hours during business days.</p>
            </InfoCard>
            
            <InfoCard>
              <InfoTitle>Our Location</InfoTitle>
              <p>Based in Paris, working globally with travel businesses across Europe, Africa, Asia, and the Americas.</p>
              <MapFrame>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d83998.95410693044!2d2.2770201483069833!3d48.85883773930445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b70f%3A0x40b82c3688c9460!2sParis%2C%20France!5e0!3m2!1sen!2sus!4v1651245244895!5m2!1sen!2sus" 
                  width="100%" 
                  height="100%" 
                  style={{border: "0"}}
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Map of Paris, France"
                />
              </MapFrame>
            </InfoCard>
          </ContactInfo>
        </ContactGrid>
        
        <CTASection>
          <CTATitle>Need Immediate Assistance?</CTATitle>
          <CTAText>
            For urgent inquiries or to schedule a quick call to discuss your project, check out our available tools or browse our projects for inspiration.
          </CTAText>
          <CTAButton to="/tools">Explore Our Tools</CTAButton>
        </CTASection>
      </Container>
    </ContactContainer>
  );
};

export default Contact;